/**
 * jadeui/src/api/mcp.js
 * 
 * MCP API client - handles all MCP backend requests
 */

const API_BASE = '/api/mcp';

/**
 * MCP API client
 */
export const mcpApi = {
  // ============== Health & Status ==============
  
  async getHealth() {
    const res = await fetch(`${API_BASE}/health`);
    if (!res.ok) throw new Error('Health check failed');
    return res.json();
  },

  async getStatus() {
    const res = await fetch(`${API_BASE}/status`);
    if (!res.ok) throw new Error('Status check failed');
    return res.json();
  },

  // ============== Server Management ==============

  async connectStdioServer(name, command, env = null) {
    const res = await fetch(`${API_BASE}/servers/connect/stdio`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, command, env }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Connection failed');
    return data;
  },

  async connectSSEServer(name, url, headers = null) {
    const res = await fetch(`${API_BASE}/servers/connect/sse`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ name, url, headers }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Connection failed');
    return data;
  },

  async getServers() {
    const res = await fetch(`${API_BASE}/servers`);
    if (!res.ok) throw new Error('Failed to fetch servers');
    return res.json();
  },

  async disconnectServer(name) {
    const res = await fetch(`${API_BASE}/servers/${name}`, { method: 'DELETE' });
    if (!res.ok) throw new Error('Disconnect failed');
    return res.json();
  },

  async disconnectAllServers() {
    const res = await fetch(`${API_BASE}/servers/disconnect-all`, { method: 'POST' });
    if (!res.ok) throw new Error('Disconnect all failed');
    return res.json();
  },

  // ============== Tools ==============

  async getTools() {
    const res = await fetch(`${API_BASE}/tools`);
    if (!res.ok) throw new Error('Failed to fetch tools');
    return res.json();
  },

  async getServerTools(serverName) {
    const res = await fetch(`${API_BASE}/tools/${serverName}`);
    if (!res.ok) throw new Error('Failed to fetch server tools');
    return res.json();
  },

  async invokeTool(server, tool, args) {
    const res = await fetch(`${API_BASE}/tools/invoke`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ server, tool, arguments: args }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Tool invocation failed');
    return data;
  },

  // ============== Agent Query ==============

  async query(queryText, options = {}) {
    const res = await fetch(`${API_BASE}/query`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        query: queryText,
        include_db_context: options.includeDbContext || false,
        db_table: options.dbTable || null,
      }),
    });
    const data = await res.json();
    if (!res.ok) throw new Error(data.detail || 'Query failed');
    return data;
  },

  // Stream query via SSE
  streamQuery(queryText) {
    const url = `${API_BASE}/query/stream?query=${encodeURIComponent(queryText)}`;
    return new EventSource(url);
  },

  // ============== History ==============

  async getHistory() {
    const res = await fetch(`${API_BASE}/history`);
    if (!res.ok) throw new Error('Failed to fetch history');
    return res.json();
  },

  async clearHistory() {
    const res = await fetch(`${API_BASE}/history/clear`, { method: 'POST' });
    if (!res.ok) throw new Error('Failed to clear history');
    return res.json();
  },

  // ============== Models ==============

  async getModels() {
    const res = await fetch(`${API_BASE}/models`);
    if (!res.ok) throw new Error('Failed to fetch models');
    return res.json();
  },

  async setModel(model) {
    const res = await fetch(`${API_BASE}/models`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ model }),
    });
    if (!res.ok) throw new Error('Failed to set model');
    return res.json();
  },

  // ============== WebSocket ==============

  createWebSocket() {
    const protocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
    return new WebSocket(`${protocol}//${window.location.host}${API_BASE}/ws`);
  },
};

export default mcpApi;
